'use client';

import { useEffect, useState } from 'react';
import { isSellableCountry } from '@/lib/geo/sellable';

/**
 * Renders one of two branches depending on whether we can sell to the visitor.
 *
 * Needed because the pages this guards are force-static: their HTML is shared
 * across every country, so the choice has to happen in the browser.
 *
 * Both branches are decided AFTER the lookup resolves, and `sellable` is what
 * renders first. That order is deliberate — crawlers and no-JS clients see the
 * quote CTA, which is the correct indexable content and the recoverable
 * failure. The ad branch only mounts once we know the visitor cannot buy, so
 * an ad unit is never mounted and then hidden.
 */

interface GeoGateProps {
  /** Shown to visitors in a country we can sell to (and before we know). */
  sellable: React.ReactNode;
  /** Shown once we know the visitor is outside our selling countries. */
  fallback: React.ReactNode;
}

const CACHE_KEY = 'ihq:geo:sellable';

export default function GeoGate({ sellable, fallback }: GeoGateProps) {
  const [canSell, setCanSell] = useState<boolean>(true);

  useEffect(() => {
    // One lookup per session: the answer cannot change mid-visit, and the KB
    // is browsed several pages deep.
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached !== null) {
        setCanSell(cached === '1');
        return;
      }
    } catch {
      // Private mode or blocked storage — fall through to the network.
    }

    let cancelled = false;
    fetch('/api/geo')
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data) return;
        const ok = isSellableCountry(data.country);
        setCanSell(ok);
        try {
          sessionStorage.setItem(CACHE_KEY, ok ? '1' : '0');
        } catch {
          // Not being able to cache is not a reason to fail.
        }
      })
      .catch(() => {
        // Lookup failed — keep showing the CTA rather than guessing.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return <>{canSell ? sellable : fallback}</>;
}
